import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { instantToDate } from '@airreading/common';
import { PlusCircle } from 'lucide-react';
import { useState } from 'react';
import { DayPickerRangeProps } from 'react-day-picker';
import { Temporal } from 'temporal-polyfill';

export interface TableFilterDateRangePickerProps {
  calendarProps?: Omit<DayPickerRangeProps, 'mode'>;
  placeholder?: string;
  fromPlainDateString?: string;
  toPlainDateString?: string;
  onRangeSelect: (rangeInPlainDateString: { fromPlainDateString?: string; toPlainDateString?: string }) => void;
  buttonTestId?: string;
}

export const TableFilterPlainDateRangePicker = ({
  calendarProps = {},
  placeholder,
  fromPlainDateString,
  toPlainDateString,
  onRangeSelect,
  buttonTestId,
}: TableFilterDateRangePickerProps) => {
  const { ...othersCalendarProps } = calendarProps;
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  return (
    <>
      <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 border-dashed" data-testid={buttonTestId}>
            <PlusCircle className="mr-2 h-4 w-4" />
            {fromPlainDateString || toPlainDateString ? (
              <span>{`${fromPlainDateString || ''} -- ${toPlainDateString || ''}`}</span>
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            {...othersCalendarProps}
            onSelect={(range) => {
              onRangeSelect?.({
                fromPlainDateString: range?.from
                  ? Temporal.Instant.from(range.from.toISOString())
                      .toZonedDateTimeISO(Temporal.Now.timeZoneId())
                      .toPlainDate()
                      .toString()
                  : undefined,
                toPlainDateString: range?.to
                  ? Temporal.Instant.from(range.to.toISOString())
                      .toZonedDateTimeISO(Temporal.Now.timeZoneId())
                      .toPlainDate()
                      .toString()
                  : undefined,
              });
            }}
            selected={{
              from: fromPlainDateString
                ? instantToDate(
                    Temporal.PlainDate.from(fromPlainDateString)
                      .toZonedDateTime(Temporal.Now.timeZoneId())
                      .startOfDay()
                      .toInstant(),
                  )
                : undefined,
              to: toPlainDateString
                ? instantToDate(
                    Temporal.PlainDate.from(toPlainDateString)
                      .toZonedDateTime(Temporal.Now.timeZoneId())
                      .startOfDay()
                      .toInstant(),
                  )
                : undefined,
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </>
  );
};
